/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
import { useId, useState } from 'react';
import { Plus } from 'lucide-react';
import { useNavigate } from 'react-router';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { api } from '../../api';
import type { SubjectStatus, SubjectType } from '../../api/areas/accounts';
import { Callout, DataTable, FilterBar, Section, type Column } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { EntityRef } from '../../components/EntityRef';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { EmptyState, Loading } from '../../components/States';
import { formatCount, formatDate } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { SubjectFormDialog } from '../subjects/SubjectForm';
import {
  EnumBadge,
  EnumText,
  FilterChip,
  FilterSelect,
  FilterText,
  Pager,
  paths,
  RefreshButton,
  ResetFilters,
  SUBJECT_STATUS,
  TypeCountry,
  useEnumLabel,
  useListParams,
  useRecordName,
  VERIFICATION,
} from './shared';

const LIMIT = 50;
const SUBJECT_TYPES: readonly SubjectType[] = ['INDIVIDUAL', 'ORGANIZATION', 'HOUSEHOLD_BUSINESS', 'GOVERNMENT_ENTITY', 'FOREIGN_ENTITY', 'OTHER'];

type Row = Awaited<ReturnType<typeof api.listSubjects>>['items'][number];

/** `/subjects`: legal registrants. Identity numbers are never listed; see the subject page for masked hints. */
export function SubjectsPage() {
  const { t } = useTranslation('accounts');
  const label = useEnumLabel('accounts');
  useDocumentTitle(t('subjects.title'));
  const id = useId();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const list = useListParams(['q', 'subject_type', 'status', 'customer_id'] as const);
  const { q, subject_type, status, customer_id } = list.values;
  const result = useApi(
    (signal) =>
      api.listSubjects(
        {
          q: q || undefined,
          subject_type: (subject_type || undefined) as SubjectType | undefined,
          status: (status || undefined) as SubjectStatus | undefined,
          customer_id: customer_id || undefined,
          after: list.after || undefined,
          limit: LIMIT,
        },
        { signal },
      ),
    [q, subject_type, status, customer_id, list.after],
  );

  const firstRow = result.status === 'success' ? result.data.items[0] : undefined;
  const customerName = useRecordName('customer', customer_id, firstRow?.managing_customer_id === customer_id ? firstRow.managing_customer_name : null);

  const columns: Column<Row>[] = [
    {
      key: 'name',
      header: t('subjects.col.legalName'),
      cell: (s) => (
        <EntityRef
          to={paths.subject(s.id)}
          id={s.id}
          label={s.legal_name ?? <span className="italic">{t('subjects.noVersion')}</span>}
          sub={s.current_version_number ? t('subjects.versionN', { n: s.current_version_number }) : undefined}
          className="min-w-40 max-w-64"
        />
      ),
      className: 'whitespace-normal',
    },
    { key: 'type', header: t('subjects.col.typeCountry'), cell: (s) => <TypeCountry type={<EnumText group="subjectType" value={s.subject_type} />} country={s.country_code} /> },
    { key: 'status', header: t('subjects.col.status'), cell: (s) => <EnumBadge group="subjectStatus" tones={SUBJECT_STATUS} value={s.status} /> },
    {
      key: 'verification',
      header: t('subjects.col.verification'),
      cell: (s) => (s.latest_verification_status ? <EnumBadge group="verification" tones={VERIFICATION} value={s.latest_verification_status} /> : <span className="text-muted-foreground">{t('common.none')}</span>),
    },
    {
      key: 'managedBy',
      header: t('subjects.col.managedBy'),
      // Long account names wrap so the date column stays in view.
      className: 'whitespace-normal',
      cell: (s) =>
        s.managing_customer_id ? (
          <EntityRef to={paths.customer(s.managing_customer_id)} id={s.managing_customer_id} label={s.managing_customer_name ?? undefined} className="min-w-36 max-w-56" />
        ) : (
          <span className="text-muted-foreground">{s.hostbill_client_id ? `HostBill #${s.hostbill_client_id}` : '—'}</span>
        ),
    },
    { key: 'domains', header: t('subjects.col.domains'), numeric: true, cell: (s) => formatCount(s.domain_count) },
    { key: 'created', header: t('subjects.col.created'), className: 'whitespace-nowrap', cell: (s) => formatDate(s.created_at) },
  ];

  return (
    <>
      <PageHeader title={t('subjects.title')} crumbs={[{ label: t('crumb') }]} subtitle={t('subjects.subtitle')} actions={
          <>
            <Button size="sm" onClick={() => setCreating(true)}>
              <Plus className="size-4" aria-hidden="true" /> {t('subjectForm.createButton')}
            </Button>
            <RefreshButton onClick={result.reload} />
          </>
        }
      />
      <SubjectFormDialog open={creating} onOpenChange={setCreating} customerId={customer_id} onSaved={(sid) => void navigate(paths.subject(sid))} />
      <Callout tone="info" className="mb-4">
        {t('subjects.piiNote')}
      </Callout>
      <FilterBar actions={<ResetFilters show={list.filtered} onReset={list.reset} />}>
        <FilterText id={`${id}-q`} label={t('filter.search')} value={q} placeholder={t('subjects.searchPlaceholder')} onApply={(v) => list.setFilter('q', v)} />
        <FilterSelect id={`${id}-t`} label={t('filter.type')} value={subject_type} options={SUBJECT_TYPES.map((s) => [s, label('subjectType', s)] as const)} onChange={(v) => list.setFilter('subject_type', v)} />
        <FilterSelect
          id={`${id}-s`}
          label={t('filter.status')}
          value={status}
          options={Object.keys(SUBJECT_STATUS).map((s) => [s, label('subjectStatus', s)] as const)}
          onChange={(v) => list.setFilter('status', v)}
        />
        {customer_id ? <FilterChip label={t('filter.customer', { name: customerName })} title={customer_id} onClear={() => list.setFilter('customer_id', '')} /> : null}
      </FilterBar>
      <Section flush>
        {result.status === 'loading' ? (
          <div className="px-5">
            <Loading rows={5} />
          </div>
        ) : null}
        {result.status === 'error' ? (
          <div className="px-5 pb-5">
            <DataError error={result.error} onRetry={result.reload} title={t('subjects.loadError')} />
          </div>
        ) : null}
        {result.status === 'success' ? (
          <>
            <DataTable columns={columns} rows={result.data.items} rowKey={(s) => s.id} empty={<EmptyState title={t('subjects.empty')} />} />
            {result.data.items.length > 0 ? <Pager shown={result.data.items.length} hasPrev={Boolean(list.after)} nextAfter={result.data.next_after} onPrev={list.prev} onNext={list.next} /> : null}
          </>
        ) : null}
      </Section>
    </>
  );
}
